import React, { useState, useEffect } from 'react';
import { useReactFlow, Viewport } from 'reactflow';

interface GraphBookmark {
  id: string;
  name: string;
  viewport: Viewport;
  filter: {
    nodeTypes: string[];
    searchTerm: string;
  };
  timestamp: string;
}

interface GraphBookmarksProps {
  sessionId: string;
  currentFilter: {
    nodeTypes: string[];
    searchTerm: string;
  };
  onApplyBookmark: (bookmark: GraphBookmark) => void;
}

export const GraphBookmarks: React.FC<GraphBookmarksProps> = ({
  sessionId,
  currentFilter,
  onApplyBookmark
}) => {
  const [bookmarks, setBookmarks] = useState<GraphBookmark[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newBookmarkName, setNewBookmarkName] = useState('');
  const { getViewport } = useReactFlow();
  
  // Load bookmarks from localStorage
  useEffect(() => {
    const savedBookmarks = localStorage.getItem(`graph-bookmarks-${sessionId}`);
    if (savedBookmarks) {
      try {
        setBookmarks(JSON.parse(savedBookmarks));
      } catch (e) {
        console.error('Failed to parse bookmarks:', e);
      }
    }
  }, [sessionId]);
  
  // Save bookmarks to localStorage
  const saveBookmarks = (bookmarks: GraphBookmark[]) => {
    localStorage.setItem(`graph-bookmarks-${sessionId}`, JSON.stringify(bookmarks));
    setBookmarks(bookmarks);
  };
  
  // Create a new bookmark
  const createBookmark = () => {
    if (!newBookmarkName.trim()) return;
    
    const newBookmark: GraphBookmark = {
      id: `bookmark-${Date.now()}`,
      name: newBookmarkName,
      viewport: getViewport(),
      filter: { ...currentFilter },
      timestamp: new Date().toISOString()
    };
    
    saveBookmarks([...bookmarks, newBookmark]);
    setNewBookmarkName('');
  };
  
  // Delete a bookmark
  const deleteBookmark = (id: string) => {
    saveBookmarks(bookmarks.filter(bookmark => bookmark.id !== id));
  };
  
  // Apply a bookmark
  const applyBookmark = (bookmark: GraphBookmark) => {
    onApplyBookmark(bookmark);
    setIsOpen(false);
  };
  
  return (
    <div className="graph-bookmarks relative">
      <button
        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
        </svg>
        Bookmarks
      </button>
      
      {isOpen && (
        <div className="absolute top-10 right-0 bg-white rounded-lg shadow-lg p-4 z-20 w-80">
          <h3 className="font-medium mb-3">Graph Bookmarks</h3>
          
          <div className="mb-4">
            <div className="flex">
              <input
                type="text"
                className="flex-grow px-3 py-1 border border-gray-300 rounded-l-md"
                placeholder="Bookmark name"
                value={newBookmarkName}
                onChange={(e) => setNewBookmarkName(e.target.value)}
              />
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                onClick={createBookmark}
                disabled={!newBookmarkName.trim()}
              >
                Save
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Saves current view, position, and filters
            </p>
          </div>
          
          {bookmarks.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {bookmarks.map(bookmark => (
                <div 
                  key={bookmark.id}
                  className="p-2 border border-gray-200 rounded-md hover:bg-gray-50 flex justify-between items-center"
                >
                  <div>
                    <div className="font-medium">{bookmark.name}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(bookmark.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => applyBookmark(bookmark)}
                    >
                      Apply
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800"
                      onClick={() => deleteBookmark(bookmark.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No bookmarks saved yet
            </div>
          )}
        </div>
      )}
    </div>
  );
};
